import { get } from 'lodash';
import { instance } from './api.service';
import holidays from '../../actions/holidays';

/**
 * Card #254 — Holiday & Blackout Dates.
 *
 * The order form reads the calendar SYNCHRONOUSLY from inside antd's
 * `disabledDate` predicate, which runs during render and cannot await. So the
 * document is cached on `window.BLACKOUTDATES` and read through
 * `getBlackoutConfig()`, exactly mirroring how `getConfig` reads
 * `window.APPCONFIG` (app/src/actions/utils.js:624).
 *
 * `loadBlackoutDates()` is called once at app bootstrap (app/src/config/setup.js,
 * alongside loadConfig) and again whenever the admin page saves, so an edit is
 * live for the rest of the session without a reload.
 */

// FAIL OPEN. Every failure mode — request error, logged-out boot, a `data` row
// left null by some future migration — must land on "nothing is blacked out".
// An empty calendar blocks nothing; a broken read that blocked everything would
// take the whole scheduling flow down.
const EMPTY = { presets: [], weekdays: [], entries: [] };

export const getBlackoutDates = async () => {
  const resp = await instance.get('/blackoutdates');
  return holidays.normalizeConfig(get(resp, 'data.data', EMPTY));
};

export const loadBlackoutDates = async () => {
  const cfg = await getBlackoutDates().catch(() => EMPTY);
  window.BLACKOUTDATES = cfg;
  return cfg;
};

/**
 * The current calendar, normalized and always safe to read. Returns the empty
 * calendar before the bootstrap fetch resolves.
 */
export const getBlackoutConfig = () => holidays.normalizeConfig(window.BLACKOUTDATES);

/**
 * Anything a form field may hold -> a bare `YYYY-MM-DD` calendar day, or null.
 *
 * A moment is formatted with its own LOCAL day: `app/src` has zero
 * moment-timezone usage, so the day the user clicked is the day compared, in
 * their own browser zone, which is the only day they can meaningfully mean.
 *
 * A string has its day part taken verbatim, exactly as the app's own
 * `getMidDate` does (it splits on 'T'). Nothing is ever re-anchored to UTC —
 * that is precisely where the off-by-one would come from.
 */
export const toBlackoutDayString = (date) => {
  if (!date) { return null; }
  if (typeof date.format === 'function') { return date.format('YYYY-MM-DD'); }
  if (typeof date === 'string' && holidays.isDateStr(date.slice(0, 10))) { return date.slice(0, 10); }
  return null;
};

/**
 * A date field's value -> `{ kind, label, id }` when that calendar day is
 * blacked out, or null when it is not.
 */
export const describeBlackoutForMoment = (date, cfg) => {
  const day = toBlackoutDayString(date);
  if (!day) { return null; }
  return holidays.describeBlackout(day, cfg || getBlackoutConfig());
};

export const createBlackoutEntry = async (entry) => {
  const resp = await instance.post('/blackoutdates/create', { entry });
  return get(resp, 'data.data', EMPTY);
};

export const updateBlackoutEntry = async (entry) => {
  const resp = await instance.post('/blackoutdates/update', { entry });
  return get(resp, 'data.data', EMPTY);
};

export const deleteBlackoutEntry = async (entry) => {
  const resp = await instance.post('/blackoutdates/delete', { entry });
  return get(resp, 'data.data', EMPTY);
};

export const saveBlackoutSettings = async ({ presets, weekdays }) => {
  const resp = await instance.post('/blackoutdates/settings', { presets, weekdays });
  return get(resp, 'data.data', EMPTY);
};
